import { Box, Button, Center, Group, Loader, Stack } from '@mantine/core';
import { IconCalendarEvent } from '@tabler/icons-react';
import { NextLink as Link } from '~/components/NextLink/NextLink';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import { MetricTimeframe } from '~/shared/utils/prisma/enums';
import { useRouter } from 'next/router';
import React, { useState } from 'react';

import { NotFound } from '~/components/AppLayout/NotFound';
import { SortFilter } from '~/components/Filters';
import { MasonryContainer } from '~/components/MasonryColumns/MasonryContainer';
import { MasonryProvider } from '~/components/MasonryColumns/MasonryProvider';
import PostsInfinite from '~/components/Post/Infinite/PostsInfinite';
import { usePostQueryParams } from '~/components/Post/post.utils';
import { constants } from '~/server/common/constants';
import { PostSort } from '~/server/common/enums';
import { isViewer } from '~/utils/is-viewer';
import { trpc } from '~/utils/trpc';

import { useCurrentUser } from '~/hooks/useCurrentUser';
import { FeedContentToggle } from '~/components/FeedContentToggle/FeedContentToggle';
import { PostFiltersDropdown } from '~/components/Post/Infinite/PostFiltersDropdown';
import { ActiveTagFilter } from '~/components/Tags/ActiveTagFilter';
import { UserProfileLayout } from '~/components/Profile/ProfileLayout2';
import { Page } from '~/components/AppLayout/Page';
import { createServerSideProps } from '~/server/utils/server-side-helpers';
import { getUserBannedAtByUsername } from '~/server/services/user-banned-at.service';
import { jumpToEmptyMessage } from '~/shared/utils/jump-to-month';

export const getServerSideProps = createServerSideProps({
  useSSG: true,
  resolver: async ({ ctx, ssg }) => {
    const username = ctx.query.username as string;
    const user = await getUserBannedAtByUsername(username);

    if (user?.bannedAt)
      return {
        redirect: { destination: `/user/${username}`, permanent: true },
      };

    await Promise.all([
      ssg?.userProfile.get.prefetch({ username }),
      ssg?.userProfile.overview.prefetch({ username }),
    ]);
  },
});

function UserPostsPage() {
  const router = useRouter();
  const username = (router.query.username as string) ?? '';
  // The account the profile header shows, looked up by the name as stored. The posts query
  // takes its id: the slug of some older usernames names no account.
  const { data: user, isLoading } = trpc.userProfile.get.useQuery(
    { username },
    { enabled: !!username }
  );

  if (!username) return <NotFound />;
  if (isLoading)
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  if (!user) return <NotFound />;

  return <UserPostsFeed profileUserId={user.id} />;
}

function UserPostsFeed({ profileUserId }: { profileUserId: number }) {
  const currentUser = useCurrentUser();
  const {
    replace,
    query: { followed = false, section: querySection, ...query },
  } = usePostQueryParams();
  // const { replace, section: querySection, ...queryFilters } = usePostQueryParams();
  const period = query.period ?? MetricTimeframe.AllTime;
  const sort = query.sort ?? PostSort.Newest;
  const selfView = isViewer(currentUser, profileUserId);
  // A moderator sees anything the creator can see of their own. Matches the
  // images and videos tabs; the server authorizes this independently in
  // `post.service.ts` and does not trust the control being rendered.
  const canViewDrafts = selfView || (currentUser?.isModerator ?? false);
  const features = useFeatureFlags();

  const [section, setSection] = useState<'published' | 'draft'>(
    canViewDrafts ? querySection ?? 'published' : 'published'
  );
  const viewingDraft = section === 'draft';
  const effectiveScheduled = viewingDraft ? query.scheduled ?? true : query.scheduled;

  return (
    <Box mt="md">
      <MasonryProvider
        columnWidth={constants.cardSizes.image}
        maxColumnCount={7}
        maxSingleColumnWidth={450}
      >
        <MasonryContainer p={0}>
          <Stack gap="xs">
            <Group gap={8} justify="space-between">
              {canViewDrafts && (
                <FeedContentToggle
                  size="xs"
                  value={section}
                  onChange={(section) => {
                    const nextSection = section as 'published' | 'draft';
                    setSection(nextSection);
                    replace({ section: nextSection, scheduled: undefined });
                  }}
                />
              )}
              {selfView && features.postPlanner && (
                <Button
                  component={Link}
                  href="/user/schedule"
                  size="xs"
                  variant="light"
                  leftSection={<IconCalendarEvent size={14} />}
                >
                  Open planner
                </Button>
              )}
              {!selfView && <ActiveTagFilter tagIds={query.tags ?? []} />}
              <Group gap={8} ml="auto" wrap="nowrap">
                {/* Drafts are a publish queue with a fixed order (see post-sort.ts), so there is
                    nothing for the picker to change there. */}
                {!viewingDraft && (
                  <SortFilter
                    type="posts"
                    value={sort}
                    onChange={(x) => replace({ sort: x as PostSort })}
                  />
                )}
                <PostFiltersDropdown
                  query={{ ...query, period, followed, scheduled: effectiveScheduled }}
                  onChange={(filters) => replace(filters)}
                  showScheduled={canViewDrafts}
                  jumpToSort={viewingDraft ? undefined : sort}
                  size="compact-sm"
                />
              </Group>
            </Group>
            <PostsInfinite
              filters={{
                ...query,
                username: undefined,
                userId: profileUserId,
                followed,
                period,
                sort,
                scheduled: effectiveScheduled,
                draftOnly: viewingDraft,
                pending: true,
              }}
              emptyMessage={viewingDraft ? undefined : jumpToEmptyMessage(query.jumpTo, sort)}
            />
          </Stack>
        </MasonryContainer>
      </MasonryProvider>
    </Box>
  );
}

export default Page(UserPostsPage, { getLayout: UserProfileLayout });
